import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ChatbotWidget } from "@/components/chatbot/ChatbotWidget";
import { CookieConsent } from "@/components/compliance/CookieConsent";
import { lazy, Suspense, useState, useEffect } from "react";
import SkipToContent from "./components/a11y/SkipToContent";
import { RouteAnnouncer } from "./components/a11y/RouteAnnouncer";
import { CommandPalette } from "@/components/ui/command-palette";
import Navigation from "./components/layout/Navigation";
import Footer from "./components/layout/Footer";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AdminOnboarding } from "@/components/onboarding/AdminOnboarding";
import { ApplicantGuard } from "@/components/applicant/ApplicantGuard";
import { ApplicantLayout } from "@/components/applicant/ApplicantLayout";
import { ApplicantOnboarding } from "@/components/onboarding/ApplicantOnboarding";
import { AdvisorGuard } from "@/components/advisor/AdvisorGuard";
import { AdvisorLayout } from "@/components/advisor/AdvisorLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTransition } from "@/components/transitions/PageTransition";
import { ScrollToTopOnRouteChange } from "@/components/navigation/ScrollToTopOnRouteChange";
import { SessionTimeoutWarning } from "@/components/security/SessionTimeoutWarning";
import { SecurityHeadersCheck } from "@/components/security/SecurityHeadersCheck";
import { MFAPrompt } from "@/components/admin/MFAPrompt";
import { generateCSRFToken } from "@/lib/csrf";

// Lazy load route components for better performance
const Index = lazy(() => import("./pages/Index"));
const Programs = lazy(() => import("./pages/Programs"));
const Tuition = lazy(() => import("./pages/Tuition"));
const Admissions = lazy(() => import("./pages/Admissions"));
const About = lazy(() => import("./pages/About"));
const Faculty = lazy(() => import("./pages/Faculty"));
const FacultyProfile = lazy(() => import("./pages/FacultyProfile"));
const Contact = lazy(() => import("./pages/Contact"));
const Library = lazy(() => import("./pages/Library"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ApplicationSuccess = lazy(() => import("./pages/ApplicationSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminApplications = lazy(() => import("./pages/admin/Applications"));
const AdminApplicationDetail = lazy(() => import("./pages/admin/ApplicationDetail"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminEnrollments = lazy(() => import("./pages/admin/Enrollments"));
const AdminCourseManagement = lazy(() => import("./pages/admin/CourseManagement"));
const AdminRoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const AdminRBACManagement = lazy(() => import("./pages/admin/RBACManagement"));
const AdminProgramManagement = lazy(() => import("./pages/admin/ProgramManagement"));
const AdminTuitionManagement = lazy(() => import("./pages/admin/TuitionManagement"));
const AdminAnnouncementsManagement = lazy(() => import("./pages/admin/AnnouncementsManagement"));
const AdminCalendarEventsManagement = lazy(() => import("./pages/admin/CalendarEventsManagement"));
const AdminFAQManagement = lazy(() => import("./pages/admin/FAQManagement"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminDeploymentGuide = lazy(() => import("./pages/admin/DeploymentGuide"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminIntegrations = lazy(() => import("./pages/admin/Integrations"));

// Applicant portal pages
const ApplicantDashboard = lazy(() => import("./pages/applicant/Dashboard"));
const ApplicantDocuments = lazy(() => import("./pages/applicant/Documents"));
const ApplicantProfile = lazy(() => import("./pages/applicant/Profile"));
const ApplicantMessages = lazy(() => import("./pages/applicant/Messages"));
const ApplicantPayments = lazy(() => import("./pages/applicant/Payments"));
const ApplicantEnrollment = lazy(() => import("./pages/applicant/Enrollment"));
const ApplicantCourses = lazy(() => import("./pages/applicant/Courses"));
const ApplicantHousing = lazy(() => import("./pages/applicant/Housing"));
const ApplicantFinancialAid = lazy(() => import("./pages/applicant/FinancialAid"));

// Auth pages
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// Advisor pages
const AdvisorDashboard = lazy(() => import("./pages/advisor/Dashboard"));
const AdvisorLeads = lazy(() => import("./pages/advisor/Leads"));
const AdvisorApplications = lazy(() => import("./pages/advisor/Applications"));
const AdvisorFollowUps = lazy(() => import("./pages/advisor/FollowUps"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  const [commandOpen, setCommandOpen] = useState(false);

  // Initialize CSRF token on app load
  useEffect(() => {
    generateCSRFToken();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTopOnRouteChange />
            <RouteAnnouncer />
            <SkipToContent />
            <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
            <SessionTimeoutWarning />
            <SecurityHeadersCheck />
            <MFAPrompt />
            <div className="flex flex-col min-h-screen">
              <Navigation onCommandOpen={() => setCommandOpen(true)} />
              <main id="main" className="flex-1" role="main" aria-label="Main content">
                <Suspense fallback={<PageLoader />}>
                  <PageTransition>
                    <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/programs" element={<Programs />} />
                  <Route path="/tuition" element={<Tuition />} />
                  <Route path="/admissions" element={<Admissions />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/application-success" element={<ApplicationSuccess />} />
                  <Route path="/faculty" element={<Faculty />} />
                  <Route path="/faculty/:slug" element={<FacultyProfile />} />
                  
                  {/* Auth Routes */}
                  <Route path="/auth/login" element={<Login />} />
                  <Route path="/auth/signup" element={<Signup />} />
                  <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  
                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <AdminGuard>
                        <AdminLayout />
                      </AdminGuard>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="applications" element={<AdminApplications />} />
                    <Route path="applications/:id" element={<AdminApplicationDetail />} />
                    <Route path="enrollments" element={<AdminEnrollments />} />
                    <Route path="courses" element={<AdminCourseManagement />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="roles" element={<AdminRoleManagement />} />
                    <Route path="rbac" element={<AdminRBACManagement />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="reports" element={<AdminReports />} />
                    <Route path="integrations" element={<AdminIntegrations />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="deployment" element={<AdminDeploymentGuide />} />
                    <Route path="programs" element={<AdminProgramManagement />} />
                    <Route path="tuition-pricing" element={<AdminTuitionManagement />} />
                    <Route path="announcements" element={<AdminAnnouncementsManagement />} />
                    <Route path="calendar-events" element={<AdminCalendarEventsManagement />} />
                    <Route path="faqs" element={<AdminFAQManagement />} />
                  </Route>

                  {/* Applicant Portal Routes */}
                  <Route
                    path="/portal/*"
                    element={
                      <ApplicantGuard>
                        <ApplicantLayout />
                      </ApplicantGuard>
                    }
                  >
                    <Route index element={<ApplicantDashboard />} />
                    <Route path="enrollment" element={<ApplicantEnrollment />} />
                    <Route path="courses" element={<ApplicantCourses />} />
                    <Route path="housing" element={<ApplicantHousing />} />
                    <Route path="financial-aid" element={<ApplicantFinancialAid />} />
                    <Route path="documents" element={<ApplicantDocuments />} />
                    <Route path="profile" element={<ApplicantProfile />} />
                    <Route path="messages" element={<ApplicantMessages />} />
                    <Route path="payments" element={<ApplicantPayments />} />
                  </Route>

                  {/* Advisors Portal Routes */}
                  <Route
                    path="/advisor/*"
                    element={
                      <AdvisorGuard>
                        <AdvisorLayout />
                      </AdvisorGuard>
                    }
                  >
                    <Route index element={<AdvisorDashboard />} />
                    <Route path="dashboard" element={<AdvisorDashboard />} />
                    <Route path="leads" element={<AdvisorLeads />} />
                    <Route path="applications" element={<AdvisorApplications />} />
                    <Route path="follow-ups" element={<AdvisorFollowUps />} />
                  </Route>
                  
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </PageTransition>
                </Suspense>
              </main>
              <Footer />
          </div>
          <ChatbotWidget />
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
